import { EYEBROW_FONT_SIZE } from "./blocks/eyebrow";
import { surfaceHasBackgroundImage } from "./block-surface";
import { resolveButtonChrome } from "./button-theme";
import { resolveRowColumnOverflowIssues } from "./column-content-min";
import { resolveRowLayoutWidth } from "./block-width";
import { forEachBlock } from "./document-blocks";
import {
  meetsContrastAA,
  resolveBlockSurfaceColor,
} from "./presentation";
import {
  resolveDividerColor,
  resolveEyebrowRuleColor,
  resolveEyebrowTextColor,
  resolveLinkColor,
  resolveTextColor,
  type TextTheme,
} from "./text-theme";
import { textHtmlHasLinks } from "./sanitize";
import type { Block, EmailDocument, Row } from "./types";
import { isGroup, isRow } from "./sections";

export type ExportLintSeverity = "error" | "warning";

export type ExportLintIssue = {
  severity: ExportLintSeverity;
  message: string;
  blockId?: string;
};

function hasLinkHref(href: string): boolean {
  const trimmed = href.trim();
  return trimmed.length > 0 && trimmed !== "#";
}

function usesContentSurface(
  surfaceColor: string,
  contentBackgroundColor: string,
): boolean {
  return surfaceColor === contentBackgroundColor;
}

function pushContrastIssue(
  issues: ExportLintIssue[],
  message: string,
  foreground: string,
  background: string,
  largeText: boolean,
  blockId?: string,
): void {
  if (meetsContrastAA(foreground, background, largeText)) {
    return;
  }

  issues.push({
    severity: "warning",
    message,
    blockId,
  });
}

function columnOverflowLintMessage(block: Block): string {
  if (block.type === "button" || block.type === "buttonRow") {
    return "Button may overflow its column on desktop.";
  }

  return "Content may overflow its column on desktop.";
}

function pushColumnOverflowIssues(
  issues: ExportLintIssue[],
  document: EmailDocument,
  row: Row,
  cache: Map<string, ReturnType<typeof resolveRowColumnOverflowIssues>>,
): void {
  if (row.columns.length <= 1) {
    return;
  }

  let rowIssues = cache.get(row.id);
  if (rowIssues === undefined) {
    const layout = resolveRowLayoutWidth(document, row.id);
    rowIssues =
      layout === undefined
        ? []
        : resolveRowColumnOverflowIssues(
            layout.row,
            layout.innerWidth,
            document.settings.bodyFontFamily,
          );
    cache.set(row.id, rowIssues);
  }

  for (const issue of rowIssues) {
    let block: Block | undefined;
    for (const column of row.columns) {
      block = column.blocks.find(item => item.id === issue.blockId);
      if (block) {
        break;
      }
    }
    if (!block) {
      continue;
    }

    issues.push({
      severity: "warning",
      message: columnOverflowLintMessage(block),
      blockId: issue.blockId,
    });
  }
}

export function lintExportDocument(document: EmailDocument): ExportLintIssue[] {
  const issues: ExportLintIssue[] = [];
  const { settings } = document;
  const textTheme: TextTheme = settings;
  let h1Count = 0;

  pushContrastIssue(
    issues,
    "Heading text color may not meet 4.5:1 contrast on the content background.",
    settings.headingColor,
    settings.contentBackgroundColor,
    false,
  );
  pushContrastIssue(
    issues,
    "Paragraph text color may not meet 4.5:1 contrast on the content background.",
    settings.paragraphColor,
    settings.contentBackgroundColor,
    false,
  );

  let hasButtonBlocks = false;
  let hasTextBlockLinks = false;

  forEachBlock(document, ({ block, row, column, group }) => {
    const surfaceColor = resolveBlockSurfaceColor(
      column.backgroundColor,
      row.backgroundColor,
      group?.backgroundColor,
      settings.contentBackgroundColor,
    );
    const onContentSurface = usesContentSurface(
      surfaceColor,
      settings.contentBackgroundColor,
    );
    const onBackgroundImage = surfaceHasBackgroundImage(
      column,
      row,
      group?.backgroundImage,
    );

    if (block.type === "text") {
      if (block.variant === "h1") {
        h1Count += 1;
      }

      const largeText = block.fontSize >= 18;
      if (!onBackgroundImage && !(onContentSurface && block.color == null)) {
        pushContrastIssue(
          issues,
          "Text color may not meet contrast requirements on this block background.",
          resolveTextColor(block, textTheme),
          surfaceColor,
          largeText,
          block.id,
        );
      }

      if (textHtmlHasLinks(block.html)) {
        hasTextBlockLinks = true;

        if (
          !onBackgroundImage &&
          !(onContentSurface && block.linkColor == null)
        ) {
          pushContrastIssue(
            issues,
            "Link color may not meet contrast requirements on this block background.",
            resolveLinkColor(block, textTheme),
            surfaceColor,
            false,
            block.id,
          );
        }
      }
    }

    if (block.type === "eyebrow") {
      const fontSize = block.fontSize ?? EYEBROW_FONT_SIZE;
      if (!onBackgroundImage && !(onContentSurface && block.color == null)) {
        pushContrastIssue(
          issues,
          "Eyebrow text color may not meet contrast requirements on this block background.",
          resolveEyebrowTextColor(block, textTheme),
          surfaceColor,
          fontSize >= 18,
          block.id,
        );
      }

      if (
        !onBackgroundImage &&
        block.rule !== "none" &&
        !(onContentSurface && block.ruleColor == null)
      ) {
        pushContrastIssue(
          issues,
          "Eyebrow rule color may not meet contrast requirements on this block background.",
          resolveEyebrowRuleColor(block, textTheme),
          surfaceColor,
          false,
          block.id,
        );
      }
    }

    if (block.type === "divider" && !onBackgroundImage) {
      pushContrastIssue(
        issues,
        "Divider color may not meet contrast requirements on this block background.",
        resolveDividerColor(block, textTheme),
        surfaceColor,
        false,
        block.id,
      );
    }

    if (block.type === "image") {
      if (hasLinkHref(block.href) && !block.alt.trim()) {
        issues.push({
          severity: "error",
          message: "Image is linked but missing alt text.",
          blockId: block.id,
        });
      }
      if (!hasLinkHref(block.href) && block.media && !block.alt.trim()) {
        issues.push({
          severity: "warning",
          message: "Image is missing alt text.",
          blockId: block.id,
        });
      }
    }

    if (block.type === "button" || block.type === "buttonRow") {
      hasButtonBlocks = true;
    }

    if (block.type === "button") {
      const chrome = resolveButtonChrome(block, settings);
      pushContrastIssue(
        issues,
        "Button label color may not meet contrast requirements.",
        chrome.color,
        chrome.backgroundColor,
        block.fontSize >= 18,
        block.id,
      );
    }

    if (block.type === "buttonRow") {
      block.buttons.forEach((slot, index) => {
        const chrome = resolveButtonChrome(slot, settings);
        pushContrastIssue(
          issues,
          `Button row slot ${index + 1} label color may not meet contrast requirements.`,
          chrome.color,
          chrome.backgroundColor,
          slot.fontSize >= 18,
          block.id,
        );
      });
    }
  });

  if (hasTextBlockLinks) {
    pushContrastIssue(
      issues,
      "Link color may not meet 4.5:1 contrast on the content background.",
      settings.linkColor,
      settings.contentBackgroundColor,
      false,
    );
  }

  if (hasButtonBlocks) {
    pushContrastIssue(
      issues,
      "Default button text color may not meet contrast requirements on the default button fill.",
      settings.buttonTextColor,
      settings.buttonFillColor,
      false,
    );
  }

  if (h1Count > 1) {
    issues.push({
      severity: "warning",
      message: `Email has ${h1Count} Heading 1 blocks. Use at most one H1 per email.`,
    });
  }

  const overflowCache = new Map<
    string,
    ReturnType<typeof resolveRowColumnOverflowIssues>
  >();
  for (const section of document.sections) {
    const rows = isGroup(section)
      ? section.rows
      : isRow(section)
        ? [section]
        : [];
    for (const row of rows) {
      pushColumnOverflowIssues(issues, document, row, overflowCache);
    }
  }

  return issues;
}
